import { ArrowRightOutlined, EditOutlined, FileOutlined, MoreOutlined } from '@ant-design/icons';
import {
  Alert,
  App as AntApp,
  Breadcrumb,
  Button,
  Card,
  Col,
  Descriptions,
  Dropdown,
  Flex,
  Input,
  InputNumber,
  Progress,
  Row,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  type MenuProps,
} from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from '../../lib/toast';
import { customerTypeLabel, hasBillingAccess, hasDesignAccess, hasQuoteAccess } from '../access';
import * as api from '../api';
import { AttachmentUploadButton, CommentRows } from '../components';
import { formatDate, formatDateTime, formatINR, todayISO } from '../format';
import { useWorkOrder, useZenBootstrap, zenKeys } from '../hooks';
import { orderProgress, orderStatus, orderedTrackTasks, taskStatus, taskTimeInState } from '../status';
import type { Comment, DepartmentTask, WorkOrder } from '../types';
import { DueText, EmptyState, OrderNumber, PageSkeleton, StatusPill, TaskTrack } from '../ui';
import { useTaskActions } from '../useTaskActions';
import { orderValue } from '../views';

const { Text, Paragraph } = Typography;

type DetailTab = 'overview' | 'items' | 'files' | 'billing' | 'activity';

interface ActivityEvent {
  id: string;
  at: string;
  text: ReactNode;
  tone: 'signal' | 'action' | 'muted';
}

function activityFor(order: WorkOrder, comments: Comment[]): ActivityEvent[] {
  const events: ActivityEvent[] = [
    { id: 'created', at: order.createdAt, text: <>Order received · {order.creatorName}</>, tone: 'muted' },
  ];
  for (const task of order.tasks) {
    if (task.startedAt) events.push({ id: `${task.id}-start`, at: task.startedAt, text: <>{task.departmentName} started{task.pickedUpByName ? ` · ${task.pickedUpByName}` : ''}</>, tone: 'action' });
    if (task.finishedAt) events.push({ id: `${task.id}-finish`, at: task.finishedAt, text: <>{task.departmentName} done{task.finishedByName ? ` · ${task.finishedByName}` : ''}</>, tone: 'action' });
  }
  for (const comment of comments) {
    events.push({ id: comment.id, at: comment.createdAt, text: <>Note from {comment.userName}: “{comment.text.length > 80 ? `${comment.text.slice(0, 80)}…` : comment.text}”</>, tone: 'muted' });
  }
  if (order.status === 'cancelled') events.push({ id: 'cancelled', at: order.updatedAt, text: 'Order cancelled', tone: 'signal' });
  if (order.deletedAt) events.push({ id: 'deleted', at: order.deletedAt, text: 'Order deleted', tone: 'signal' });
  return events.sort((left, right) => new Date(right.at).getTime() - new Date(left.at).getTime());
}

function Timeline({ events, limit }: { events: ActivityEvent[]; limit?: number }) {
  const shown = limit ? events.slice(0, limit) : events;
  return (
    <ol className="ua-timeline">
      {shown.map((event) => (
        <li key={event.id} className={`ua-timeline-item ua-timeline-item--${event.tone}`}>
          <span className="ua-timeline-text">{event.text}</span>
          <span className="ua-timeline-time">{formatDateTime(event.at)}</span>
        </li>
      ))}
    </ol>
  );
}

export function WorkOrderDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useWorkOrder(id);
  const { data: bootstrap } = useZenBootstrap();
  const { modal } = AntApp.useApp();
  const queryClient = useQueryClient();
  const runTaskAction = useTaskActions(bootstrap?.currentUser);
  const [tab, setTab] = useState<DetailTab>('overview');
  const [comment, setComment] = useState('');
  const [quote, setQuote] = useState({ amount: 0, notes: '' });
  const [invoice, setInvoice] = useState({ number: '', amount: 0, date: todayISO() });
  const [payment, setPayment] = useState({ amount: 0, date: todayISO(), reference: '', notes: '' });
  const [savingBilling, setSavingBilling] = useState<string | null>(null);
  if (isLoading || !data || !bootstrap) return <PageSkeleton />;

  const order: WorkOrder = data.workOrder;
  const user = bootstrap.currentUser;
  const isAdmin = user.isAdmin;
  const isCreator = order.creatorId === user.id;
  const canManageBilling = hasBillingAccess(user);
  // Sales can also open colleagues' orders that reach their Design/OTC/Delivery queue,
  // but only edit and quote the ones they created.
  const canManageQuote = canManageBilling || (hasQuoteAccess(user) && isCreator);
  const isCancelled = order.status === 'cancelled';
  const isDeleted = Boolean(order.deletedAt);
  const isReadOnly = isCancelled || isDeleted;
  const canEdit = !isReadOnly && order.status === 'active' && (isAdmin || isCreator || canManageBilling);
  const canUpload = hasDesignAccess(user);
  const status = orderStatus(order);
  const progress = orderProgress(order);
  const events = activityFor(order, data.comments);
  const value = orderValue(order);

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: zenKeys.order(id) }),
      queryClient.invalidateQueries({ queryKey: zenKeys.bootstrap }),
    ]);
  };

  const saveBilling = async (key: string, run: () => Promise<unknown>, success: string) => {
    setSavingBilling(key);
    try {
      await run();
      await refresh();
      toast.success(success);
      return true;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Couldn’t save. Check the amounts and try again.');
      return false;
    } finally {
      setSavingBilling(null);
    }
  };

  const confirmCancel = () => modal.confirm({
    title: `Cancel ${order.orderNumber}?`,
    className: 'zen-responsive-confirm',
    content: (
      <Space orientation="vertical" size={12} style={{ width: '100%' }}>
        <Text>Cancelling is permanent. Tasks, notes, files and billing lock for good; existing history stays visible.</Text>
        <Input.TextArea id="cancel-reason" placeholder="Reason (optional)" />
      </Space>
    ),
    okText: 'Cancel order',
    cancelText: 'Keep order',
    okButtonProps: { danger: true },
    onOk: async () => {
      const reason = (document.getElementById('cancel-reason') as HTMLTextAreaElement | null)?.value ?? '';
      try {
        await api.cancelWorkOrder(order.id, reason);
        await refresh();
        toast.success(`${order.orderNumber} cancelled`);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : `Couldn’t cancel ${order.orderNumber}.`);
        throw error;
      }
    },
  });

  const confirmDelete = () => modal.confirm({
    title: `Delete ${order.orderNumber}?`,
    className: 'zen-responsive-confirm',
    content: 'It disappears from every active screen. Administrators can still find it, with its history, under Deleted.',
    okText: 'Delete order',
    cancelText: 'Keep order',
    okButtonProps: { danger: true },
    onOk: async () => {
      try {
        await api.deleteWorkOrder(order.id);
        await queryClient.invalidateQueries({ queryKey: zenKeys.bootstrap });
        navigate('/work-orders/deleted', { replace: true });
      } catch (error) {
        toast.error(error instanceof Error ? error.message : `Couldn’t delete ${order.orderNumber}.`);
        throw error;
      }
    },
  });

  // One primary action: the next thing this person should do with this order.
  const needsInvoice = canManageBilling && !order.accountsApproved && !isReadOnly;
  const primaryAction = needsInvoice
    ? <Button type="primary" icon={<ArrowRightOutlined />} iconPlacement="end" onClick={() => setTab('billing')}>Record invoice</Button>
    : canEdit
      ? <Link to={`/work-orders/${order.id}/edit`}><Button type="primary" icon={<EditOutlined />}>Edit</Button></Link>
      : null;
  const moreItems: MenuProps['items'] = [
    ...(needsInvoice && canEdit ? [{ key: 'edit', label: <Link to={`/work-orders/${order.id}/edit`}>Edit</Link> }] : []),
    ...(!isReadOnly && (isCreator || isAdmin) && order.status === 'active' ? [{ key: 'cancel', label: 'Cancel order', danger: true, onClick: confirmCancel }] : []),
    ...(isAdmin && !isDeleted ? [{ key: 'delete', label: 'Delete order', danger: true, onClick: confirmDelete }] : []),
  ];

  const recoveryMenu = (task: DepartmentTask): MenuProps['items'] => [
    ...(task.status === 'started' ? [{ key: 'release', label: 'Release to Pending', onClick: () => runTaskAction(task, 'admin-release', order) }] : []),
    ...(task.status !== 'completed' ? [{ key: 'complete', label: 'Mark done', onClick: () => runTaskAction(task, 'admin-complete', order) }] : []),
    ...(task.status === 'completed' ? [{ key: 'reopen', label: 'Reopen', onClick: () => runTaskAction(task, 'admin-reopen', order) }] : []),
  ];

  const banners = (
    <>
      {isDeleted && (
        <Alert type="error" showIcon title="Deleted · read-only" description={`Deleted ${order.deletedAt ? formatDateTime(order.deletedAt) : ''}. Tasks, files, notes, billing and history are kept.`} />
      )}
      {isCancelled && !isDeleted && (
        <Alert type="error" showIcon title="Cancelled · read-only" description={order.cancelledReason ? `Reason: ${order.cancelledReason}` : 'No reason was recorded. History is kept, but nothing more can change.'} />
      )}
      {!isReadOnly && !order.accountsApproved && (
        <Alert type="warning" showIcon title="Waiting on Accounts" description="Design and department work open once Accounts records the invoice number, amount and date." />
      )}
    </>
  );

  const overview = (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      <Descriptions className="zen-responsive-descriptions" size="small" column={{ xs: 1, sm: 2 }} colon={false}>
        <Descriptions.Item label="Customer">
          {canManageBilling ? <Link to={`/accounts/${order.clientId}`}>{order.customerName}</Link> : order.customerName}
        </Descriptions.Item>
        <Descriptions.Item label="Customer type">{customerTypeLabel(order.customerType)}</Descriptions.Item>
        <Descriptions.Item label="Phone">{order.customerPhone ? <span className="ua-mono">{order.customerPhone}</span> : 'Not given'}</Descriptions.Item>
        <Descriptions.Item label="Order date">{formatDate(order.orderDate)}</Descriptions.Item>
        <Descriptions.Item label="Customer address">{order.customerAddress || 'Not given'}</Descriptions.Item>
        <Descriptions.Item label="Delivery address">{order.deliveryAddress || 'Not given'}</Descriptions.Item>
        <Descriptions.Item label="Contact / designer">{order.contactPerson || '—'}</Descriptions.Item>
        <Descriptions.Item label="Created by">{order.creatorName}</Descriptions.Item>
        {order.installation.required && <Descriptions.Item label="Installation / delivery" span={{ xs: 1, sm: 2 }}>{order.installation.details || 'Required'}</Descriptions.Item>}
        {order.repair.required && <Descriptions.Item label="Repair" span={{ xs: 1, sm: 2 }}>{order.repair.details || 'Required'}</Descriptions.Item>}
      </Descriptions>
      {order.items.map((item, index) => (
        <div key={item.id} className="ua-detail-item">
          <div className="ua-overline">Item {index + 1}</div>
          <Paragraph style={{ whiteSpace: 'pre-wrap', margin: '4px 0 12px' }}>{item.description}</Paragraph>
          <TaskTrack tasks={orderedTrackTasks(item.tasks)} />
        </div>
      ))}
    </Space>
  );

  const items = (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      {order.items.map((item, index) => (
        <Card key={item.id} size="small" title={`Item ${index + 1}`} extra={<Space size={4} wrap>{item.departmentIds.map((departmentId) => <Tag key={departmentId}>{bootstrap.departments.find((department) => department.id === departmentId)?.name ?? departmentId}</Tag>)}</Space>}>
          <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{item.description}</Paragraph>
          <Table
            className="zen-responsive-table"
            rowKey="id"
            size="small"
            dataSource={orderedTrackTasks(item.tasks)}
            scroll={{ x: 'max-content' }}
            pagination={false}
            columns={[
              { title: 'Department', dataIndex: 'departmentName', render: (name: string) => <Text strong>{name}</Text> },
              {
                title: 'Status',
                key: 'status',
                render: (_, task) => task.availableAt || task.status !== 'pending'
                  ? <StatusPill status={taskStatus(task)} />
                  : <StatusPill tone="waiting">{order.accountsApproved ? 'Waiting' : 'Waiting on Accounts'}</StatusPill>,
              },
              { title: 'Time', key: 'time', render: (_, task) => <Text type="secondary">{taskTimeInState(task) ?? '—'}</Text> },
              ...(isAdmin && !isReadOnly ? [{
                title: '',
                key: 'recovery',
                width: 48,
                render: (_: unknown, task: DepartmentTask) => (
                  <Dropdown menu={{ items: recoveryMenu(task) }} trigger={['click']}>
                    <Button type="text" size="small" icon={<MoreOutlined />} aria-label={`Recovery actions for ${task.departmentName}`} />
                  </Dropdown>
                ),
              }] : []),
            ]}
          />
        </Card>
      ))}
    </Space>
  );

  const files = (
    <Space orientation="vertical" size={12} style={{ width: '100%' }}>
      {!isReadOnly && canUpload && <div><AttachmentUploadButton workOrderId={order.id} label="Upload files" /></div>}
      {data.attachments.length ? (
        <div className="zen-list" role="list">
          {data.attachments.map((file) => (
            <div key={file.id} className="zen-list-row" role="listitem">
              <button type="button" className="zen-list-main zen-list-main--interactive" onClick={() => api.downloadFile(order.id, file.id, file.name)}>
                <span className="zen-list-leading"><FileOutlined /></span>
                <span className="zen-list-copy">
                  <span className="zen-list-title">{file.name}</span>
                  <span className="zen-list-description">{file.uploadedByName} · {formatDateTime(file.uploadedAt)}</span>
                </span>
              </button>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState compact title="No files yet" description={canUpload && !isReadOnly ? 'Upload artwork, proofs or site photos here.' : 'Files added by the team appear here.'} />
      )}
    </Space>
  );

  const billing = (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      {isReadOnly && <Alert type="warning" showIcon title="Billing is frozen" description="Records stay visible, but nothing can change and no order payments can be added." />}
      {canManageBilling && (
        <Card size="small" title="Invoice" extra={order.invoice ? <StatusPill tone="success">Recorded</StatusPill> : <StatusPill tone="warning">Needed</StatusPill>}>
          {order.invoice && (
            <Paragraph><span className="ua-mono">{order.invoice.number}</span> · {formatINR(order.invoice.amount)} · {formatDate(order.invoice.date)}</Paragraph>
          )}
          <div className="form-grid">
            <div className="form-group"><label>Invoice number <span className="ua-required">*</span></label><Input disabled={isReadOnly} placeholder="INV/26-27/118" value={invoice.number} onChange={(event) => setInvoice({ ...invoice, number: event.target.value })} /></div>
            <div className="form-group"><label>Amount (₹) <span className="ua-required">*</span></label><InputNumber disabled={isReadOnly} min={0} value={invoice.amount} onChange={(next) => setInvoice({ ...invoice, amount: Number(next) })} formatter={(next) => (next ? Number(next).toLocaleString('en-IN') : '')} parser={(next) => Number((next ?? '').replace(/[^\d.]/g, ''))} prefix="₹" style={{ width: '100%' }} /></div>
            <div className="form-group"><label>Invoice date <span className="ua-required">*</span></label><Input disabled={isReadOnly} type="date" value={invoice.date} onChange={(event) => setInvoice({ ...invoice, date: event.target.value })} /></div>
          </div>
          <Button
            type={needsInvoice ? 'primary' : 'default'}
            disabled={isReadOnly || !invoice.number.trim() || invoice.amount <= 0 || !invoice.date}
            loading={savingBilling === 'invoice'}
            onClick={() => saveBilling('invoice', () => api.saveInvoice(order.id, invoice), order.invoice ? 'Invoice updated' : `Invoice recorded. ${order.orderNumber} is released to Design.`)}
          >
            {order.invoice ? 'Update invoice' : 'Record invoice'}
          </Button>
        </Card>
      )}
      {canManageQuote && (
        <Card size="small" title="Quote" extra={<Text type="secondary">Optional</Text>}>
          {order.quote && <Paragraph>{formatINR(order.quote.amount)}{order.quote.notes ? ` · ${order.quote.notes}` : ''}</Paragraph>}
          <Space orientation="vertical" size={8} style={{ width: '100%' }}>
            <InputNumber disabled={isReadOnly} min={0} value={quote.amount} onChange={(next) => setQuote({ ...quote, amount: Number(next) })} formatter={(next) => (next ? Number(next).toLocaleString('en-IN') : '')} parser={(next) => Number((next ?? '').replace(/[^\d.]/g, ''))} prefix="₹" style={{ width: '100%' }} />
            <Input.TextArea disabled={isReadOnly} placeholder="Short notes" value={quote.notes} onChange={(event) => setQuote({ ...quote, notes: event.target.value })} />
            <Button disabled={isReadOnly || quote.amount <= 0} loading={savingBilling === 'quote'} onClick={() => saveBilling('quote', () => api.saveQuote(order.id, quote), 'Quote saved')}>Save quote</Button>
          </Space>
        </Card>
      )}
      {canManageBilling && (
        <Card size="small" title="Record payment">
          <div className="form-grid">
            <div className="form-group"><label>Amount (₹)</label><InputNumber disabled={isReadOnly} min={0} value={payment.amount} onChange={(next) => setPayment({ ...payment, amount: Number(next) })} formatter={(next) => (next ? Number(next).toLocaleString('en-IN') : '')} parser={(next) => Number((next ?? '').replace(/[^\d.]/g, ''))} prefix="₹" style={{ width: '100%' }} /></div>
            <div className="form-group"><label>Date</label><Input disabled={isReadOnly} type="date" value={payment.date} onChange={(event) => setPayment({ ...payment, date: event.target.value })} /></div>
            <div className="form-group"><label>Reference</label><Input disabled={isReadOnly} placeholder="UPI / cheque number" value={payment.reference} onChange={(event) => setPayment({ ...payment, reference: event.target.value })} /></div>
            <div className="form-group full"><label>Notes</label><Input.TextArea disabled={isReadOnly} value={payment.notes} onChange={(event) => setPayment({ ...payment, notes: event.target.value })} /></div>
          </div>
          <Button
            disabled={isReadOnly || payment.amount <= 0}
            loading={savingBilling === 'payment'}
            onClick={async () => {
              const ok = await saveBilling('payment', () => api.addLedgerEntry({ clientId: order.clientId, workOrderId: order.id, type: 'payment', ...payment }), `Payment of ${formatINR(payment.amount)} recorded`);
              if (ok) setPayment({ amount: 0, date: todayISO(), reference: '', notes: '' });
            }}
          >
            Record payment
          </Button>
        </Card>
      )}
    </Space>
  );

  const activity = (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      {!isReadOnly && (
        <Flex className="zen-comment-composer" gap={8} align="flex-start">
          <Input.TextArea value={comment} onChange={(event) => setComment(event.target.value)} rows={2} placeholder="Add a note for everyone working on this order" />
          <Button
            disabled={!comment.trim()}
            onClick={async () => {
              try {
                await api.addComment(order.id, comment);
                setComment('');
                await refresh();
              } catch (error) {
                toast.error(error instanceof Error ? error.message : 'Couldn’t add the note. Try again.');
              }
            }}
          >
            Add note
          </Button>
        </Flex>
      )}
      <CommentRows comments={data.comments} />
      <div>
        <div className="ua-overline" style={{ marginBottom: 8 }}>History</div>
        <Timeline events={events} />
      </div>
    </Space>
  );

  const tabs = [
    { key: 'overview', label: 'Overview', children: overview },
    { key: 'items', label: <>Items <span className="ua-tab-count">{order.items.length}</span></>, children: items },
    { key: 'files', label: <>Files <span className="ua-tab-count">{data.attachments.length}</span></>, children: files },
    ...(canManageBilling || canManageQuote ? [{ key: 'billing', label: 'Billing', children: billing }] : []),
    { key: 'activity', label: 'Activity', children: activity },
  ];

  return (
    <Flex vertical gap={16}>
      <Breadcrumb items={[{ title: <Link to="/work-orders">Work orders</Link> }, { title: <span className="ua-mono">{order.orderNumber}</span> }]} />
      <Card className="ua-detail-header">
        <Flex justify="space-between" align="flex-start" gap={16} wrap>
          <div>
            <OrderNumber>{order.orderNumber}</OrderNumber>
            <h2 className="ua-detail-title">{order.customerName}</h2>
            <StatusPill status={status} />
          </div>
          <Space className="zen-detail-actions" wrap>
            {primaryAction}
            {moreItems.length > 0 && (
              <Dropdown menu={{ items: moreItems }} trigger={['click']}>
                <Button icon={<MoreOutlined />} aria-label="More actions">More</Button>
              </Dropdown>
            )}
          </Space>
        </Flex>
      </Card>
      {banners}
      <Row gutter={[16, 16]} align="top">
        <Col xs={24} xl={16}>
          <Card>
            <Tabs activeKey={tab} onChange={(key) => setTab(key as DetailTab)} items={tabs} />
          </Card>
        </Col>
        <Col xs={24} xl={8}>
          <div className="ua-summary-rail">
            <Card size="small">
              <div className="ua-overline">Due</div>
              <div className="ua-rail-figure"><DueText date={order.deliveryDate} done={order.status !== 'active'} /></div>
              <Text type="secondary">{formatDate(order.deliveryDate)}</Text>
            </Card>
            <Card size="small">
              <div className="ua-overline">Progress</div>
              <div className="ua-rail-figure">{progress.done} of {progress.total} tasks</div>
              <Progress percent={progress.total ? Math.round((progress.done / progress.total) * 100) : 0} showInfo={false} size="small" strokeColor="var(--ua-action)" />
            </Card>
            {canManageBilling && (
              <Card size="small">
                <div className="ua-overline">Order value</div>
                <div className="ua-rail-figure ua-rail-money">{value !== null ? formatINR(value) : '—'}</div>
                {order.customerBalance !== undefined && (
                  <Text type="secondary">
                    Customer balance{' '}
                    <span className={order.customerBalance > 0 ? 'ua-text-warning' : undefined}>{formatINR(order.customerBalance)}</span>
                  </Text>
                )}
              </Card>
            )}
            <Card size="small" title="Activity" extra={<Button type="link" size="small" onClick={() => setTab('activity')}>All</Button>}>
              <Timeline events={events} limit={5} />
            </Card>
          </div>
        </Col>
      </Row>
    </Flex>
  );
}
